import {observable} from "mobx";
import api from "../../Services/Api";
import {
    LabelPrintTaskDto,
    LabelPrintTasksDto,
    LabelPrintTasksHistoryDto,
    LabelPrintTaskStatus,
    SkippedOrderDto
} from "./Dtos";

const pollIntervalMs = 3000;

class PrintLabelsWidgetStore {
    @observable orderHrefIds: string[] = [];
    @observable isLoading: boolean = true;
    @observable isPrinting: boolean = false;

    /** задачи, созданные этим нажатием на печать */
    @observable printedTasks: LabelPrintTaskDto[] = [];
    @observable history: LabelPrintTaskDto[] = [];
    @observable skipped: SkippedOrderDto[] = [];

    private pollTimer?: number;
    /** попап открывают повторно, а ответы прошлой сессии не должны подмешиваться в новую */
    private session: number = 0;

    async init(contextKey: string, orderHrefIds: string[]) {
        this.cleanup();
        const session = this.session;
        this.orderHrefIds = orderHrefIds;
        this.isLoading = true;
        try {
            const authSuccess = await api.auth(contextKey);
            if (authSuccess && session === this.session) {
                await this.loadTasks(session);
            }
        } finally {
            if (session === this.session) {
                this.isLoading = false;
            }
        }
    }

    async print() {
        const session = this.session;
        this.isPrinting = true;
        try {
            const result = await api.post("labels/create", {orderHrefIds: this.orderHrefIds});
            if (!result.success || session !== this.session) {
                return;
            }

            const created = result.data as LabelPrintTasksDto;
            this.skipped = created.skipped;
            this.printedTasks = created.tasks;
            await this.loadTasks(session);
        } finally {
            if (session === this.session) {
                this.isPrinting = false;
            }
        }
    }

    get hasTasksInProgress() {
        return this.printedTasks.some(x => isInProgress(x));
    }

    /**
     * Модалку открывают и без выделения — ради истории; печатать тогда нечего
     */
    get canPrint() {
        return this.orderHrefIds.length > 0 && !this.isPrinting && !this.hasTasksInProgress;
    }

    get printedOrdersCount() {
        return this.printedTasks.reduce((sum, task) => sum + task.ordersCount, 0);
    }

    getDownloadUrl(taskId: string) {
        return `/api/labels/${taskId}/download`;
    }

    cleanup() {
        this.session++;
        this.stopPoll();
        this.orderHrefIds = [];
        this.printedTasks = [];
        this.history = [];
        this.skipped = [];
        this.isPrinting = false;
    }

    private async loadTasks(session: number) {
        try {
            const result = await api.get("labels/tasks");
            if (session !== this.session) {
                return;
            }

            if (result.success) {
                const tasks = result.data as LabelPrintTasksHistoryDto;
                this.history = tasks.history;
                if (this.printedTasks.length === 0) {
                    // попап открыли заново, пока задачи ещё формируются — показываем их, а не пустой экран
                    this.printedTasks = tasks.active;
                } else {
                    this.refreshPrintedTasks(tasks);
                }
            }
        } finally {
            // опрос продолжаем даже после неудачного запроса, иначе задача навсегда останется «формируется»
            if (session === this.session) {
                this.schedulePoll(session);
            }
        }
    }

    /**
     * Задача, ушедшая из активных и не попавшая в историю, сформироваться не смогла
     */
    private refreshPrintedTasks(tasks: LabelPrintTasksHistoryDto) {
        const known = tasks.active.concat(tasks.history);
        this.printedTasks = this.printedTasks.map(printed => {
            const actual = known.filter(x => x.id === printed.id);
            if (actual.length > 0) {
                return actual[0];
            }

            return isInProgress(printed) ? {...printed, status: LabelPrintTaskStatus.Failed} : printed;
        });
    }

    private schedulePoll(session: number) {
        this.stopPoll();
        if (!this.hasTasksInProgress) {
            return;
        }

        this.pollTimer = window.setTimeout(() => void this.loadTasks(session), pollIntervalMs);
    }

    private stopPoll() {
        if (this.pollTimer) {
            window.clearTimeout(this.pollTimer);
            this.pollTimer = undefined;
        }
    }
}

export function isInProgress(task: LabelPrintTaskDto) {
    return task.status === LabelPrintTaskStatus.Created || task.status === LabelPrintTaskStatus.Generating;
}

const printLabelsWidgetStore = new PrintLabelsWidgetStore();
export default printLabelsWidgetStore;
